import React, { useRef, useState } from 'react'
import { useNavigate, useLocation, useParams, Link } from 'react-router'
import { observer } from 'mobx-react-lite'
import {
    Row, Text, Icon, Button,
    FloatingMenu, MenuItem, useUI
} from '@evertel/web/ui'
import { DocumentIcon } from '../../elements/DocumentIcon'
import { DocIconPickerModal } from './DocIconPickerModal'
import { SchemaBuilderController } from '../../../controller'

const DESC_PLACEHOLDER = 'Add a short description...'
interface SettingsBarProps {
    schemaBuilderController: SchemaBuilderController
}
interface LocationState {
    from?: string
    isAdmin?: boolean
}

const SettingsBar: React.FC<SettingsBarProps> = observer(({
    schemaBuilderController
}) => {

    const { target } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const locationState = location.state as LocationState | null
    const docName = useRef<HTMLSpanElement>(null)
    const docDesc = useRef<HTMLSpanElement>(null)
    const { addToast, isConfirmed } = useUI()

    const [showDocIconPickerModal, setShowDocIconPickerModal] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const onFocus = (field: string) => {
        if (field === 'name') {
            if (docName.current?.innerText === schemaBuilderController.defaultName) {
                docName.current.innerText = ''
            }
        } else if (field === 'description') {
            if (docDesc.current?.innerText === DESC_PLACEHOLDER) {
                docDesc.current.innerText = ''
            }
        }
    }

    const onBlur = (field: string) => {
        if (field === 'name' && docName.current) {
            const name = docName.current?.innerText?.trim()
            schemaBuilderController.setName(name || schemaBuilderController.defaultName)

        } else if (field === 'description' && docDesc.current) {
            const description = docDesc.current?.innerText?.trim()
            schemaBuilderController.setDescription(description || DESC_PLACEHOLDER)
        }
    }

    const onSave = async () => {
        const isNew = !schemaBuilderController.id

        if (schemaBuilderController.name === schemaBuilderController.defaultName || !schemaBuilderController.name) {
            await isConfirmed({
                title: 'Please name your template',
                message: 'Please name your template before saving',
                acceptButton: {
                    label: 'Ok',
                    color: 'success'
                }
            })
        } else {
            setIsSaving(true)
            try {
                const schema = await schemaBuilderController.save()

                addToast({
                    color: 'success',
                    message: 'Your template has been saved.'
                })

                if (schema?.id && isNew) navigate(`/everdocs/schema/edit/${schema?.id}`)

            } catch (error: any) {
                addToast({
                    color: 'danger',
                    message: error.message
                })
            }
            setIsSaving(false)
        }
    }

    const getBackLinkTo = () => {
        if (locationState?.from) {
            return {
                pathname: locationState.from,
                state: { isAdmin: locationState.isAdmin }
            }
        } else if (target === 'global') {
            return '/everdocs/schema/templates/global'
        } else {
            return '/everdocs'
        }
    }

    return (
        <Row
            align="between"
            valign="center"
            className="schema-builder-settings-bar">
            <Link to={getBackLinkTo()} style={{ textDecoration: 'none' }}>
                <Button>
                    <Icon
                        name="chevron-left"
                        color="white"
                        className="me-2"
                    />
                    Back
                </Button>
            </Link>

            <Row valign="center">
                <Icon
                    name="pen"
                    color="muted"
                />
                <div className="d-flex flex-column align-items-center">
                    <span
                        ref={docName}
                        id="name"
                        role="textbox"
                        contentEditable
                        suppressContentEditableWarning={true}
                        onBlur={() => onBlur('name')}
                        onFocus={() => onFocus('name')}
                        className="doc-name">
                        {schemaBuilderController.name}
                    </span>
                    <span
                        ref={docDesc}
                        id="description"
                        role="textbox"
                        contentEditable
                        suppressContentEditableWarning={true}
                        onFocus={() => onFocus('description')}
                        onBlur={() => onBlur('description')}
                        className="doc-description">
                        {schemaBuilderController.description || DESC_PLACEHOLDER}
                    </span>
                </div>
                <button
                    onClick={() => setShowDocIconPickerModal(true)}
                    className="btn btn-link mx-3"
                >
                    <DocumentIcon
                        schema={{
                            icon: schemaBuilderController.icon,
                            color: schemaBuilderController.color
                        }}
                        width={30}
                        height={30}
                    />
                </button>
            </Row>
            <Row>
                <button
                    className="me-4 p-2"
                    onClick={() => schemaBuilderController.setIsEnabled(!schemaBuilderController.isEnabled)}
                    style={{ minWidth: 100 }}
                >
                    <Icon
                        name={schemaBuilderController.isEnabled ? 'toggle-on' : 'toggle-off'}
                        color={schemaBuilderController.isEnabled ? 'success' : 'danger'}
                        className="me-2"
                    />
                    <span className="text-white">
                        {schemaBuilderController.isEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                </button>
                <Button
                    color="primary"
                    className="fw-bold"
                    loading={isSaving}
                    onClick={onSave}>
                    Save
                </Button>
            </Row>

            <DocIconPickerModal
                visible={showDocIconPickerModal}
                onClose={() => setShowDocIconPickerModal(false)}
                controller={schemaBuilderController}
            />
        </Row>
    )
})

export {
    SettingsBar
}
